import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import AutoComplete from 'material-ui/AutoComplete/AutoComplete';
import IconButton from 'material-ui/IconButton';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import DropDownIcon from 'material-ui/svg-icons/navigation/arrow-drop-down';
import * as Colors from 'material-ui/styles/colors';
import _ from 'lodash';
import {findDOMNode} from 'react-dom';
import EventListener from 'react-event-listener';
import {defaultMemoize} from 'reselect';
import EllipsisTooltipText from 'shared/components/EllipsisTooltipText';
import MenuItem from 'material-ui/MenuItem';

const BUTTON_SIZE = 24;
const INITIAL_MAX_ITEMS_IN_SEARCH = 10;
const NO_RESULTS_FOUND = 'No results found';
const NO_RESULTS_FOUND_FILTER = (searchText, key) => {
  return key === NO_RESULTS_FOUND;
};

export const styles = {};

export default class SingleSelect extends PureComponent {
  static contextTypes = {
    getScrollableContainer: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.getTextByValues = _.memoize(dataSource => {
      const {dataSourceConfig} = this.props;
      const textByValues = _.mapValues(
        _.keyBy(dataSource, dataSourceConfig.value),
        value => value[dataSourceConfig.text]
      );
      textByValues[-1] = NO_RESULTS_FOUND;
      return textByValues;
    });
    const {dataSource, dataSourceConfig} = props;
    this.state = {
      id: _.uniqueId('singleSelect'),
      value: props.value,
      searchText: dataSourceConfig ? this.getTextByValues(dataSource)[props.value] : props.value,
      hintAsSearch: false,
      hover: false,
      noResults: false,
      maxItemsInSearch: INITIAL_MAX_ITEMS_IN_SEARCH,
    };
  }

  componentWillReceiveProps(nextProps) {
    const {value, dataSource} = nextProps;
    if (value !== this.props.value || dataSource !== this.props.dataSource) {
      this.setState({value, searchText: this.getSearchTextFromProps(nextProps)});
    }
  }

  getDataSource = defaultMemoize((dataSource, searchText) => {
    const {freeText, dataSourceConfig} = this.props;
    const {noResults} = this.state;
    const listIndex = this.getIndexFromList(searchText, dataSource);
    const noResultsFound = dataSourceConfig
      ? {
          [dataSourceConfig.text]: NO_RESULTS_FOUND,
          [dataSourceConfig.value]: -1,
        }
      : NO_RESULTS_FOUND;
    return _.map(
      _.concat(
        dataSource,
        freeText && listIndex === -1 && !_.isEmpty(searchText) ? searchText : [],
        noResults ? noResultsFound : []
      ),
      (entry, index) => {
        if (entry !== undefined) {
          const text = dataSourceConfig ? entry[dataSourceConfig.text] : entry;
          return {
            text: text,
            value: (
              <MenuItem
                onClick={() => this.handleNewRequest(entry, dataSourceConfig ? entry[dataSourceConfig.value] : index)}
                primaryText={
                  <EllipsisTooltipText
                    text={text}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    targetOrigin={{horizontal: 'right', vertical: 'top'}}
                  />
                }
              />
            ),
          };
        }
      }
    );
  });

  getSearchTextFromProps(props = this.props) {
    const {value, dataSourceConfig, dataSource} = props;
    return dataSourceConfig ? this.getTextByValues(dataSource)[value] : value;
  }

  getIndexFromList = (searchText, dataSource) => {
    if (!_.isEmpty(searchText)) {
      const trimmedValue = searchText.trim();
      const lowerData = dataSource.map(d => this.getSearchText(this.getValue(d)).toLowerCase());
      return lowerData.indexOf(trimmedValue.toLowerCase());
    }
    return -1;
  };

  getValue(request) {
    const {dataSourceConfig} = this.props;
    if (dataSourceConfig) {
      return request ? request[dataSourceConfig.value] : null;
    }
    return request;
  }

  getSearchText(value) {
    const {dataSource, dataSourceConfig} = this.props;
    if (dataSourceConfig) {
      return this.getTextByValues(dataSource)[value];
    }
    return value;
  }

  getFilter() {
    const {noResults, hintAsSearch, maxItemsInSearch} = this.state;
    let filter;
    if (noResults) {
      filter = NO_RESULTS_FOUND_FILTER;
    } else if (hintAsSearch) {
      filter = AutoComplete.noFilter;
    } else {
      filter = AutoComplete.caseInsensitiveFilter;
    }
    return this.createFilterWithMaxMatches(filter, maxItemsInSearch);
  }

  matches = {};
  lastSearchText;
  lastFilter;
  lastFilterWithMaxMatches;
  lastMaxMatches;
  /**
   * do you see what happens? do you see what happens when you **** a stranger in the ***?
   * Its very hard to get mui autoComplete NOT to render 8000 items at once. you'd think they'll have that covered, but now.
   */
  createFilterWithMaxMatches = (filter, maxMatches) => {
    if (maxMatches === Infinity) {
      return filter;
    }
    this.matches = {};
    this.lastSearchText = undefined;
    if (this.lastFilter !== filter || this.lastMaxMatches !== maxMatches) {
      this.lastMaxMatches = maxMatches;
      this.lastFilter = filter;
      this.lastFilterWithMaxMatches = (searchText, key) => {
        if (this.lastSearchText !== searchText) {
          this.lastSearchText = searchText;
          this.matches = {};
        }
        if (!this.matches[key] && _.keys(this.matches).length >= maxMatches) {
          return false;
        }
        if (filter(searchText, key)) {
          this.matches[key] = true;
          return true;
        }
        return false;
      };
    }
    return this.lastFilterWithMaxMatches;
  };

  getHintText() {
    const {freeText} = this.props;
    if (freeText) {
      return 'Search or type to add your own';
    }
    return 'Search...';
  }

  isNoResultFound(chosenRequest) {
    const {dataSourceConfig} = this.props;
    if (dataSourceConfig) {
      return chosenRequest && chosenRequest[dataSourceConfig.text] === NO_RESULTS_FOUND;
    }
    return chosenRequest === NO_RESULTS_FOUND;
  }

  handleFocus = (target = null) => {
    _.defer(() => {
      // this is the only way I found to detect scroll, the menu only exist after focus (after = defer)
      const scrollableListRef = _.get(this, 'autoComplete.refs.menu.refs.list');
      const listDomNode = findDOMNode(scrollableListRef);
      const popoverScrollableContainer = listDomNode && listDomNode.parentNode;
      if (
        popoverScrollableContainer &&
        popoverScrollableContainer.scrollHeight > popoverScrollableContainer.clientHeight
      ) {
        this.setState({popoverScrollableContainer, maxItemsInSearch: INITIAL_MAX_ITEMS_IN_SEARCH});
      } else {
        this.setState({maxItemsInSearch: Infinity});
      }
    });
    const {onFocus} = this.props;
    if (onFocus) {
      onFocus(target);
    }
    const {value} = this.state;
    this.setState({hintAsSearch: !!value, noResults: false});
  };

  handleUpdateInput = (searchText, _dataSource, {source}) => {
    if (source === 'click') {
      return;
    }
    const {dataSource, freeText} = this.props;
    let stateUpdate = {};
    if (!freeText && !_.some(dataSource, data => this.filter(searchText, data))) {
      stateUpdate = {noResults: true};
    } else {
      stateUpdate = {noResults: false};
    }
    this.setState({hintAsSearch: false, searchText, ...stateUpdate});
  };

  filter(searchText, data) {
    const {dataSourceConfig} = this.props;
    if (dataSourceConfig) {
      data = data[dataSourceConfig.text];
    }
    return AutoComplete.caseInsensitiveFilter(searchText, data);
  }

  handleNewRequest = (chosenRequest, index) => {
    const {onChange} = this.props;
    if (index === -1) {
      this.tryToChoose();
      return;
    }
    if (!chosenRequest) {
      this.setState({searchText: null, hintAsSearch: false});
    }
    if (!this.isNoResultFound(chosenRequest) && onChange) {
      onChange(this.getValue(chosenRequest));
    }
  };

  handleClose = () => {
    _.delay(this.tryToChoose, AutoComplete.defaultProps.menuCloseDelay + 10);
  };

  focus = () => {
    this.autoComplete.focus();
  };

  handleMouseEnter = () => {
    this.setState({hover: true});
  };

  handleMouseLeave = () => {
    this.setState({hover: false});
  };

  handleScroll = _.throttle(({target: {scrollTop, clientHeight, scrollHeight}}) => {
    const {maxItemsInSearch, searchText} = this.state;
    const {dataSource} = this.props;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      if (maxItemsInSearch < this.getDataSource(dataSource, searchText).length) {
        this.setState({maxItemsInSearch: maxItemsInSearch + INITIAL_MAX_ITEMS_IN_SEARCH});
      } else {
        this.setState({maxItemsInSearch: Infinity});
      }
    }
  }, 50);

  tryToChoose = () => {
    let {value} = this.state;
    let chosenRequest = value;
    const {searchText} = this.state;
    const {freeText, dataSource} = this.props;
    const listIndex = this.getIndexFromList(searchText, dataSource);
    const stateUpdate = {hintAsSearch: false};
    if (listIndex !== -1) {
      chosenRequest = dataSource[listIndex];
      value = this.getValue(chosenRequest);
      stateUpdate.searchText = this.getSearchTextFromProps({...this.props, value});
    } else if (!freeText) {
      stateUpdate.searchText = this.getSearchTextFromProps();
      value = this.props.value;
    } else {
      value = searchText;
      chosenRequest = searchText;
    }
    this.setState(stateUpdate);
    if (value !== this.props.value) {
      this.handleNewRequest(chosenRequest);
    }
  };

  renderButtons() {
    const {disabled, dataSource, style, clearable} = this.props;
    const {width} = style;
    const {hover, searchText} = this.state;
    let openButtonLeft;
    let clearButtonLeft;
    if (_.isNumber(width)) {
      openButtonLeft = width - BUTTON_SIZE;
      clearButtonLeft = !dataSource || dataSource.length === 0 ? width - BUTTON_SIZE : width - 2 * BUTTON_SIZE;
    } else {
      openButtonLeft = '90%';
      clearButtonLeft = '80%';
    }
    const buttonStyle = {width: BUTTON_SIZE, height: BUTTON_SIZE, padding: 0, position: 'absolute', bottom: 17};
    return !disabled
      ? [
          dataSource && dataSource.length === 0 ? null : (
            <IconButton
              key="openMenu"
              iconStyle={{fill: hover ? Colors.grey700 : Colors.grey400}}
              style={{left: openButtonLeft, ...buttonStyle}}
              onClick={this.focus}
            >
              <DropDownIcon />
            </IconButton>
          ),
          clearable && (
            <IconButton
              key="clear"
              iconStyle={{fill: Colors.grey700}}
              style={{
                left: clearButtonLeft,
                opacity: !disabled && hover && !_.isEmpty(searchText) ? 1 : 0,
                ...buttonStyle,
              }}
              onClick={this.handleNewRequest.bind(this, null)}
            >
              <CloseIcon />
            </IconButton>
          ),
        ]
      : null;
  }

  render() {
    const {
      clearable,
      value,
      freeText,
      dataSourceConfig,
      floatingLabelText,
      style,
      dataSource,
      disabled,
      ...other
    } = this.props;
    const {id, searchText, hintAsSearch, popoverScrollableContainer} = this.state;
    return (
      <div
        style={{display: 'flex', alignItems: 'baseline', position: 'relative', marginRight: 30}}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <AutoComplete
          id={id}
          floatingLabelText={floatingLabelText}
          dataSource={this.getDataSource(dataSource, searchText)}
          filter={this.getFilter()}
          openOnFocus
          searchText={hintAsSearch ? '' : searchText || ''}
          onUpdateInput={this.handleUpdateInput}
          style={style}
          ref={ref => {
            if (ref) this.autoComplete = ref;
          }}
          hintText={hintAsSearch ? searchText : this.getHintText()}
          floatingLabelFixed={hintAsSearch}
          hintStyle={
            hintAsSearch ? {color: Colors.grey700, transition: 'none', bottom: 11} : {transition: 'none', bottom: 11}
          }
          menuStyle={{maxHeight: '300px', overflowY: 'auto'}}
          disabled={disabled}
          fullWidth
          popoverProps={
            this.context.getScrollableContainer && this.context.getScrollableContainer()
              ? {scrollableContainer: this.context.getScrollableContainer()}
              : undefined
          }
          onKeyDown={this.handleKeyDown}
          {...other}
          onClose={this.handleClose}
          onFocus={this.handleFocus}
        />
        {this.renderButtons()}
        {popoverScrollableContainer && (
          <EventListener target={popoverScrollableContainer} onScroll={this.handleScroll} />
        )}
      </div>
    );
  }
}

SingleSelect.propTypes = {
  freeText: PropTypes.bool,
  dataSource: PropTypes.array,
  disabled: PropTypes.bool,
};

SingleSelect.defaultProps = {
  freeText: false,
  disabled: false,
  clearable: false,
};
